import { useSupabaseLoader } from "../../../../../../../server/supabase/hooks";
import type { UUID } from "../../../../../../../utils/uuid";
import type { UseSupabaseLoaderState } from "../../../../../../../server/supabase/hooks/types";
import type { SerialisedEditorDesign } from "../../../../../../message/editor/types/serialised-editor-design";

type SurveyPreviewEntry = { question: string; answer: string };

export type SurveyPreviewData = { key: string; value: SurveyPreviewEntry[] } | null;

export function useSurveyPreviewData({
  body,
}: {
  body: string | undefined;
}): UseSupabaseLoaderState<SurveyPreviewData> {
  const surveyId = body?.match(/\{\{#each survey_result_([^}]+)\}\}/)?.[1] ?? null;

  return useSupabaseLoader(
    async ({ supabase }) => {
      if (!surveyId) return null;

      const { data, error } = await supabase
        .from("survey_sample_question_answers")
        .select("answers")
        .eq("id", surveyId as UUID)
        .single();

      if (error) throw error;

      return { key: `survey_result_${surveyId}`, value: data?.answers ?? [] };
    },
    [surveyId],
  );
}

export function extractSurveyIdFromDesign(
  design: SerialisedEditorDesign | null | undefined,
): string | null {
  if (!design || design.type === "markdown") return null;

  const findSurveyId = (value: unknown): string | null => {
    if (!value || typeof value !== "object") return null;
    if (
      "type" in value &&
      value.type === "survey-answers" &&
      "surveyId" in value &&
      typeof value.surveyId === "string"
    ) {
      return value.surveyId;
    }

    for (const child of Object.values(value)) {
      const surveyId = findSurveyId(child);
      if (surveyId !== null) return surveyId;
    }

    return null;
  };

  return findSurveyId(design);
}
